import { DecimalPipe } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    ViewEncapsulation,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiTypes, RouterLinkDirective, VehicleFeature } from '@hyundai/ng-common-lib';
import { InputSelectComponent } from '@hyundai/ui';
import { Store } from '@ngrx/store';
import { DialogService } from '@pixelgenau/ngx-dialog';
import { firstValueFrom, switchMap } from 'rxjs';
import * as v from 'valibot';
import { FilterTag } from '../../components/filter/filter.tag';
import { PaginationComponent } from '../../components/pagination/pagination.component';
import { VehicleListComponent } from '../../sections/vehicle-list/vehicle-list.component';

@Component({
    standalone: true,
    templateUrl: './list-page.component.html',
    imports: [VehicleListComponent, PaginationComponent, InputSelectComponent, DecimalPipe, RouterLinkDirective],
    selector: 'lib-list-page',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export default class ListPageComponent {
    store = inject(Store);
    dialog = inject(DialogService);
    router = inject(Router);
    activatedRoute = inject(ActivatedRoute);
    titleService = inject(Title);

    list = this.store.selectSignal(VehicleFeature.vehicleState.selectLoadedList);
    numberOfVehicles = this.store.selectSignal(VehicleFeature.vehicleState.selectLoadedNumberOfVehicles);
    sortOptions = this.store.selectSignal(VehicleFeature.vehicleState.selectSortOptions);
    selectedFilter = this.store.selectSignal(VehicleFeature.vehicleState.selectFilterSelected);

    countFilter = computed(() => {
        const selectedFilter = this.selectedFilter();

        const filterArr = Object.keys(selectedFilter)
            .map((key) => key)
            .filter((key) => key !== 'sort' && key !== 'order');

        return filterArr.length;
    });

    constructor() {
        afterNextRender({
            write: () => {
                this.store.dispatch(VehicleFeature.vehicleActions.loadFilter());
            },
        });
    }

    async openFilterDialog() {
        const result = await firstValueFrom(this.dialog.openDialog(FilterTag).pipe(switchMap((d) => d.close$)));

        if (!result?.filter) return;

        this.navigateToFilter();
    }

    readonly sortSchema = v.object({
        key: v.optional(v.string()),
        order: v.optional(v.string()),
    });

    setSort(sortOrder: string) {
        const { key, order } = v.parse(this.sortSchema, JSON.parse(sortOrder)) as {
            key: ApiTypes.FilterObject['sort'];
            order: ApiTypes.FilterObject['order'];
        };

        this.store.dispatch(
            VehicleFeature.vehicleActions.addFilter({
                filterObject: {
                    sort: key,
                    order,
                },
                trackingKeys: [],
            }),
        );
        this.triggerFilter();
        this.navigateToFilter();
    }

    removeSort() {
        this.store.dispatch(
            VehicleFeature.vehicleActions.addFilter({
                filterObject: {
                    sort: undefined,
                    order: undefined,
                },
                trackingKeys: [],
            }),
        );
        this.triggerFilter();
        this.navigateToFilter();
    }

    triggerFilter() {
        this.store.dispatch(VehicleFeature.vehicleActions.filter());
    }

    navigateToFilter() {
        const filter = this.selectedFilter();
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {
                filter: Object.keys(filter).length > 0 ? JSON.stringify(filter) : undefined,
                page: 1,
            },
            replaceUrl: true,
            queryParamsHandling: 'merge',
        });
    }
}
